import { t } from "@/translations"
import { type BasketSessionState } from "@/types/centra/basket-session-state"

export const isVoucherCodeApplied = (
    vouchers: { voucher: string }[] | undefined,
    voucherCode: string
): boolean => {
    if (!Array.isArray(vouchers)) {
        return false
    }

    const normalized = voucherCode.toLowerCase()
    return vouchers.some(voucher => voucher.voucher.toLowerCase() === normalized)
}

export const isVoucherCodeAppliedInSession = (
    session: BasketSessionState | undefined,
    voucherCode: string
): boolean => {
    if (session?.status !== "active") {
        return false
    }

    return isVoucherCodeApplied(
        session.checkoutApiSelection?.selection?.discounts?.vouchers,
        voucherCode
    )
}

/**
 * Extracts vouchers from Centra selection data
 * @param selection Centra selection object
 * @returns Array of voucher objects with proper typing
 */
export const extractVouchersFromSelection = (vouchers: any[]) => {
    const extractedVouchers: {
        description: string
        voucher: string
        originalPriceOffAsNumber: number
        originalPriceOff?: string
    }[] = Array.isArray(vouchers) ? vouchers : []

    return extractedVouchers
}

/**
 * Calculates the total voucher discount display string
 * @param vouchers Array of voucher objects with priceOffAsNumber and originalPriceOff
 * @returns Formatted discount string (e.g., "10.00 USD") or false if no vouchers
 */
export const calculateTotalVoucherDiscount = (
    vouchers: {
        originalPriceOffAsNumber?: number
        originalPriceOff?: string
    }[]
): string | false => {
    if (!Array.isArray(vouchers) || vouchers.length === 0) {
        return false
    }

    const sum = vouchers.reduce(
        (acc, voucher) => acc + (voucher.originalPriceOffAsNumber ?? 0),
        0
    )
    const sample = vouchers[0]?.originalPriceOff ?? ""
    const currencyMatch = sample.match(/[A-Z]{3}$/)
    const currency = currencyMatch ? currencyMatch[0] : ""

    const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })

    const formatted = formatter.format(sum)
    return currency ? `${formatted} ${currency}` : formatted
}

// Utility function to extract shipping method name and delivery time
export const parseShippingMethodName = (fullName: string, langCode?: string) => {
    if (!fullName) {
        return {
            name: "",
            deliveryTime: null
        }
    }

    const parenthesesMatch = fullName.match(/^(.+?)\s*\((.+?)\)$/)

    if (parenthesesMatch) {
        const name = parenthesesMatch[1].trim()
        const deliveryTimeRaw = parenthesesMatch[2].trim()

        // Try to parse and translate delivery time if langCode is provided
        let deliveryTime: string | null = deliveryTimeRaw

        if (langCode) {
            // Match pattern like "2 to 5 business days" or "1 to 2 business days"
            const timeMatch = deliveryTimeRaw.match(
                /(\d+)\s+to\s+(\d+)\s+business\s+days/i
            )
            if (timeMatch) {
                const min = timeMatch[1]
                const max = timeMatch[2]
                deliveryTime = t(langCode, "checkout.shipping.min_to_max_business_days", {
                    min,
                    max
                })
            }
        }

        return {
            name,
            deliveryTime
        }
    }

    return {
        name: fullName.trim(),
        deliveryTime: null
    }
}
